import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onInput", "placeholder", "onChange", "value", "maxHeight", "disabled", "shouldAutofocus", "onPaste", "style"],
  _excluded2 = ["fontSize"];
import { forwardRef, useEffect, useRef, useLayoutEffect } from 'react';
import styled, { css } from 'styled-components';
import { callIfValid } from '../utils/callIfValid';
import { getExpandingInputBorderColor, getExpandingInputErrorBorderColor, getExpandingInputDisabledTextColor, getExpandingInputPlaceholderColor, getExpandingInputBackgroundColor } from './theme/expandingInputThemeOperators';
import { UNSTYLED, DEFAULT } from './constants/ExpandingInputVariations';
import { stripHTML } from '../utils/stripHTML';
import { jsx as _jsx } from "react/jsx-runtime";
const isHTMLEmpty = html => html.trim() === '' || html.trim() === '<br />' || html.trim() === '<br>' || html.trim() === '<br/>';
const getFocusStyles = () => css(["outline:none;"]);
const getVariationStyles = ({
  use,
  theme
}) => {
  switch (use) {
    case UNSTYLED:
      return css(["border:none;background-color:transparent;"]);
    case DEFAULT:
    default:
      return css(["border:2px solid;border-color:", ";background-color:", ";"], getExpandingInputBorderColor(theme), getExpandingInputBackgroundColor(theme));
  }
};
const getDisabledStyles = ({
  theme
}) => css(["cursor:not-allowed;color:", ";&[contenteditable][placeholder]:empty::after{cursor:not-allowed;}"], getExpandingInputDisabledTextColor(theme));
const getErrorStyles = ({
  theme
}) => css(["border:2px solid;border-color:", ";"], getExpandingInputErrorBorderColor(theme));
const StyledInput = styled.div.withConfig({
  displayName: "VizExExpandingInput__StyledInput"
})(["border-radius:3px;font-size:", ";line-height:22px;min-height:38px;max-height:", ";overflow-x:hidden;overflow-y:auto;padding:8px;position:relative;width:100%;flex-basis:100%;word-break:break-word;cursor:text;", ";", ";@supports (-webkit-touch-callout:none){cursor:pointer;}&[contenteditable][placeholder]:empty{overflow-y:hidden;}&[contenteditable][placeholder]:empty::after{display:block;content:attr(placeholder);position:relative;color:", ";background-color:transparent;cursor:text;user-select:none;font-size:", ";line-height:22px;height:22px;}@media all and (-ms-high-contrast:none){&[contenteditable][placeholder]:empty::after{position:absolute;top:50%;transform:translateY(-50%);}}:focus{", ";}", ";", ";"], ({
  fontSize
}) => fontSize || '14px', ({
  maxHeight
}) => typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight || '150px', ({
  focus
}) => focus && getFocusStyles(), getVariationStyles, ({
  theme
}) => getExpandingInputPlaceholderColor(theme), ({
  fontSize
}) => fontSize || '14px', getFocusStyles, props => props.disabled && getDisabledStyles(props), props => props.error && getErrorStyles(props));
const VizExExpandingInput = /*#__PURE__*/forwardRef((props, ref) => {
  const inputRef = /* eslint-disable-next-line react-hooks/rules-of-hooks */
  typeof ref !== 'function' && ref || useRef(null);
  const {
      onInput,
      placeholder,
      onChange,
      value,
      maxHeight,
      disabled,
      shouldAutofocus,
      onPaste,
      style
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const _ref = style || {},
    {
      fontSize
    } = _ref,
    remainingStyles = _objectWithoutPropertiesLoose(_ref, _excluded2);
  const handleInput = (event, ...eventRest) => {
    event.persist();
    if (inputRef.current && inputRef.current.innerHTML && isHTMLEmpty(inputRef.current.innerHTML)) {
      // this is to make sure the placeholder shows back up when all text is deleted from the input.
      inputRef.current.innerHTML = '';
      event.target.innerText = '';
    }
    event.target.value = event.target.innerText;
    callIfValid(onChange, event, ...eventRest);
    callIfValid(onInput, event, ...eventRest);
  };
  const handlePaste = event => {
    event.preventDefault();
    callIfValid(onPaste, event);
    if (!event.clipboardData.files || !event.clipboardData.files[0]) {
      let text;
      try {
        text = event.clipboardData.getData('text/plain');
      } catch (e) {
        // ie 11
        text = stripHTML(event.clipboardData.getData('text'));
      }
      if (document.queryCommandSupported('insertText')) {
        document.execCommand('insertText', false, text);
      } else {
        document.execCommand('paste', false, text);
      }
    }
  };
  useLayoutEffect(() => {
    if (value !== null && typeof value !== 'undefined' && inputRef.current && value !== inputRef.current.innerText) {
      inputRef.current.innerText = value || '';
    }
  }, [inputRef, value]);
  useEffect(() => {
    if (shouldAutofocus && inputRef && inputRef.current) {
      inputRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/_jsx(StyledInput, Object.assign({}, rest, {
    ref: inputRef,
    placeholder: placeholder,
    onInput: handleInput,
    onPaste: handlePaste,
    contentEditable: !disabled,
    role: "textbox",
    maxHeight: maxHeight,
    disabled: disabled,
    "aria-disabled": disabled,
    fontSize: fontSize,
    style: remainingStyles
  }));
});
VizExExpandingInput.displayName = 'VizExExpandingInput';
export default VizExExpandingInput;